import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Configure axios
axios.defaults.timeout = 15000;

// ============================================
// IMAGE API - NO CACHING, ALWAYS FRESH
// ============================================

export const fetchImageBySection = async (section) => {
  try {
    const response = await axios.get(`${API}/images/section/${section}?_t=${Date.now()}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch image for section: ${section}`, error);
    return null;
  }
};

export const fetchImagesByPage = async (page) => {
  try {
    const response = await axios.get(`${API}/images/page/${page}?_t=${Date.now()}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch images for page: ${page}`, error);
    return [];
  }
};

export const fetchAllImagesAdmin = async () => {
  const response = await axios.get(`${API}/images/admin/all?_t=${Date.now()}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createImage = async (data) => {
  const response = await axios.post(`${API}/images/admin/`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateImage = async (id, data) => {
  const response = await axios.put(`${API}/images/admin/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteImage = async (id) => {
  const response = await axios.delete(`${API}/images/admin/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// PUBLIC APIs
// ============================================

export const fetchSiteInfo = async () => {
  try {
    const response = await axios.get(`${API}/site-info`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const fetchServices = async () => {
  try {
    const response = await axios.get(`${API}/services`);
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchTestimonials = async () => {
  try {
    const response = await axios.get(`${API}/testimonials`);
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchThemeSettings = async () => {
  try {
    const response = await axios.get(`${API}/theme-settings`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const fetchGallery = async () => {
  try {
    const response = await axios.get(`${API}/gallery`);
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchAbout = async () => {
  try {
    const response = await axios.get(`${API}/about`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const fetchEtiquette = async () => {
  try {
    const response = await axios.get(`${API}/etiquette`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const fetchTravel = async () => {
  try {
    const response = await axios.get(`${API}/travel`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const fetchFaqs = async () => {
  try {
    const response = await axios.get(`${API}/faqs`);
    return response.data;
  } catch (error) {
    return [];
  }
};

export const fetchServicesLists = async () => {
  try {
    const response = await axios.get(`${API}/services-lists`);
    return response.data;
  } catch (error) {
    return { offered: [], not_offered: [] };
  }
};

export const fetchLegalContent = async (type) => {
  try {
    const response = await axios.get(`${API}/legal/${type}`);
    return response.data;
  } catch (error) {
    return null;
  }
};

export const submitContact = async (data) => {
  const response = await axios.post(`${API}/contact`, data);
  return response.data;
};

export const submitBooking = async (data) => {
  const response = await axios.post(`${API}/bookings`, data);
  return response.data;
};

// ============================================
// ADMIN AUTH
// ============================================

export const adminLogin = async (credentials) => {
  const response = await axios.post(`${API}/admin/auth/login`, credentials);
  return response.data;
};

export const changePassword = async (data) => {
  const response = await axios.post(`${API}/admin/auth/change-password`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// ADMIN SERVICES
// ============================================

export const fetchAllServicesAdmin = async () => {
  const response = await axios.get(`${API}/admin/services`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createService = async (data) => {
  const response = await axios.post(`${API}/admin/services`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateService = async (id, data) => {
  const response = await axios.put(`${API}/admin/services/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteService = async (id) => {
  const response = await axios.delete(`${API}/admin/services/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// ADMIN GALLERY
// ============================================

export const fetchAllGalleryAdmin = async () => {
  const response = await axios.get(`${API}/admin/gallery`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createGalleryItem = async (data) => {
  const response = await axios.post(`${API}/admin/gallery`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createGalleryImage = async (data) => {
  return createGalleryItem(data);
};

export const updateGalleryItem = async (id, data) => {
  const response = await axios.put(`${API}/admin/gallery/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateGalleryImage = async (id, data) => {
  return updateGalleryItem(id, data);
};

export const deleteGalleryItem = async (id) => {
  const response = await axios.delete(`${API}/admin/gallery/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteGalleryImage = async (id) => {
  return deleteGalleryItem(id);
};

// ============================================
// ADMIN TESTIMONIALS
// ============================================

export const fetchAllTestimonialsAdmin = async () => {
  const response = await axios.get(`${API}/admin/testimonials`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createTestimonial = async (data) => {
  const response = await axios.post(`${API}/admin/testimonials`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateTestimonial = async (id, data) => {
  const response = await axios.put(`${API}/admin/testimonials/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteTestimonial = async (id) => {
  const response = await axios.delete(`${API}/admin/testimonials/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// ADMIN BOOKINGS
// ============================================

export const fetchAllBookingsAdmin = async () => {
  const response = await axios.get(`${API}/admin/bookings`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const fetchBookings = async () => {
  return fetchAllBookingsAdmin();
};

export const updateBooking = async (id, data) => {
  const response = await axios.put(`${API}/admin/bookings/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateBookingStatus = async (id, status) => {
  return updateBooking(id, { status });
};

export const deleteBooking = async (id) => {
  const response = await axios.delete(`${API}/admin/bookings/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// ADMIN MESSAGES
// ============================================

export const fetchAllMessagesAdmin = async () => {
  const response = await axios.get(`${API}/admin/messages`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const fetchMessages = async () => {
  return fetchAllMessagesAdmin();
};

export const updateMessage = async (id, data) => {
  const response = await axios.put(`${API}/admin/messages/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateMessageStatus = async (id, status) => {
  return updateMessage(id, { status });
};

export const deleteMessage = async (id) => {
  const response = await axios.delete(`${API}/admin/messages/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// ADMIN SETTINGS
// ============================================

export const fetchSiteInfoAdmin = async () => {
  const response = await axios.get(`${API}/admin/site-info`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateSiteInfo = async (data) => {
  const response = await axios.put(`${API}/admin/site-info`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const fetchThemeSettingsAdmin = async () => {
  const response = await axios.get(`${API}/admin/theme-settings`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateThemeSettings = async (data) => {
  const response = await axios.put(`${API}/admin/theme-settings`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const fetchSEOSettingsAdmin = async () => {
  try {
    const response = await axios.get(`${API}/admin/seo-settings`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    return {};
  }
};

export const updateSEOSettings = async (data) => {
  const response = await axios.put(`${API}/admin/seo-settings`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const fetchServicesListsAdmin = async () => {
  try {
    const response = await axios.get(`${API}/admin/services-lists`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    return { offered: [], not_offered: [] };
  }
};

export const updateServicesLists = async (data) => {
  const response = await axios.put(`${API}/admin/services-lists`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// ADMIN PAGE SEO
// ============================================

export const fetchAllPageSEO = async () => {
  try {
    const response = await axios.get(`${API}/admin/page-seo`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    return [];
  }
};

export const updatePageSEO = async (page, data) => {
  const response = await axios.put(`${API}/admin/page-seo/${page}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============================================
// SOCIAL MEDIA
// ============================================

export const fetchSocialMedia = async () => {
  try {
    const response = await axios.get(`${API}/social-media`);
    return response.data;
  } catch (error) {
    return {};
  }
};

export const fetchSocialMediaAdmin = async () => {
  try {
    const response = await axios.get(`${API}/admin/social-media`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    return {};
  }
};

export const updateSocialMedia = async (data) => {
  const response = await axios.put(`${API}/admin/social-media`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};
